/* eslint-disable no-unused-vars */
import React, { Component, createRef } from 'react';
import propTypes from 'prop-types';
import {
  Button,
  Columns,
  Column,
  MuiTitle,
  Container,
  Field,
  Content,
} from 'src/ui/mui-ui-kit';
import {
  SolventLogo,
  SupportContactCard,
  UiToastAdapter,
  reduceServerErrors,
  LoginImage,
  TwoFactorImage,
} from 'src/ui';
import PinInput from 'react-pin-input';
import { toast } from 'react-toastify';
import { withNavigation } from 'src/ui/hoc';
import { connect } from 'react-redux';
import _ from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AsideCard } from '../components';
import {
  verifyTokenCode,
  getSupportPerson,
  sendTokenVerificationCode,
  verifyTwoFactorCode,
  resendTwoFactorCode,
} from '../services';
import { SIGN_IN_SUCCESS } from '../redux/actions';

class TwoFactorAuth extends Component {
  state = {
    verificationCode: '',
    submitting: false,
    supportContact: null,
    sendingVerificationCode: false,
    isWhiteLabel: window.location.host.includes('nexaflow'),
    email: null,
  };

  pinInputRef = createRef();

  async componentDidMount() {
    const { params, history, location } = this.props;
    const activationToken = params?.activationToken;

    // Get email from state or query parameters
    let email = location.state?.email;

    // If email is not in state, try to get it from URL parameters
    if (!email) {
      const searchParams = new URLSearchParams(location.search);
      email = searchParams.get('email');
      // console.log('Email from URL parameters:', emai l);
    }

    // If we still don't have an email, redirect back to login
    if (!email && !activationToken) {
      // console.log('No email found, redirecting to login');
      history.replace('/login');
      return;
    }

    this.setState({ email });

    if (activationToken) {
      // Existing account activation flow
      const verificationCodeResult = await sendTokenVerificationCode(
        activationToken
      );

      if (!verificationCodeResult.ok) {
        if (_.get(verificationCodeResult, 'error.status') === 406) {
          toast(
            UiToastAdapter(
              'Account is already activated. Please login.',
              'danger'
            )
          );

          history.replace('/login');
        } else if (_.get(verificationCodeResult, 'error.status') === 400) {
          toast(
            UiToastAdapter(verificationCodeResult.error.body.error, 'danger')
          );
        } else {
          reduceServerErrors(verificationCodeResult);
        }
      }

      const { ok, data: supportContact } = await getSupportPerson(
        activationToken
      );
      if (ok) {
        this.setState({ supportContact });
      }
    }
  }

  handleSubmit = async event => {
    event.preventDefault();
    const { verificationCode, email } = this.state; // Get email from state
    const { params, history, location, dispatch } = this.props;
    const activationToken = params?.activationToken;

    this.setState({ submitting: true });

    if (activationToken) {
      // Existing account activation flow
      const result = await verifyTokenCode(activationToken, verificationCode);

      if (!result.ok) {
        toast(
          UiToastAdapter(
            'Invalid authentication code. Try again or contact Solvent if the problem persists.',
            'danger'
          )
        );
        if (this.pinInputRef.current) {
          this.pinInputRef.current.clear();
        }
      } else {
        history.replace(`/create-password/${activationToken}`);
      }
    } else {
      // New login 2FA flow - use email from state instead of location.state
      if (!email) {
        console.error('No email found for 2FA verification');
        toast(
          UiToastAdapter(
            'Error: Email not found. Please try logging in again.',
            'danger'
          )
        );
        history.replace('/login');
        return;
      }

      // console.log('Verifying 2FA code for email:', email);
      const result = await verifyTwoFactorCode(email, verificationCode);

      if (!result.ok) {
        toast(UiToastAdapter('Invalid authentication code.', 'danger'));
        if (this.pinInputRef.current) {
          this.pinInputRef.current.clear();
        }
      } else {
        dispatch({ type: SIGN_IN_SUCCESS, payload: result.data });
        history.replace('/account-selection');
      }
    }

    this.setState({ submitting: false });
  };

  resendCode = async () => {
    const { params } = this.props;
    const activationToken = params?.activationToken;
    const { email } = this.state; // Get email from state

    this.setState({ sendingVerificationCode: true });

    let result;
    if (activationToken) {
      // Existing account activation flow
      result = await sendTokenVerificationCode(activationToken);
    } else {
      // New login 2FA flow - use email from state
      if (!email) {
        console.error('No email found for 2FA code resend');
        toast(
          UiToastAdapter(
            'Error: Email not found. Please try logging in again.',
            'danger'
          )
        );
        this.setState({ sendingVerificationCode: false });
        return;
      }

      // console.log('Resending 2FA code for email:', email);
      result = await resendTwoFactorCode(email);
    }

    if (!result.ok) {
      // Handle 429 throttling specifically
      if (result.error?.status === 429) {
        const errorMessage = result.error?.body?.detail || 'You are sending too many requests. Please wait a moment before trying again.';
        toast(UiToastAdapter(errorMessage, 'warning'));
      } else {
        reduceServerErrors(result);
      }
    } else {
      toast(
        UiToastAdapter(
          'A new verification code has been sent to you.',
          'success'
        )
      );
    }

    this.setState({ sendingVerificationCode: false });
  };

  render() {
    const {
      verificationCode,
      submitting,
      supportContact,
      sendingVerificationCode,
      isWhiteLabel,
    } = this.state;

    return (
      <Container
        className={
          isWhiteLabel ? 'two-factor-auth-isNexaFlow' : 'two-factor-auth'
        }
      >
        <Columns>
          <Column className="left-column">
            <div className="form-wrapper">
              {!isWhiteLabel ? null : ( // <SolventLogo />
                <img
                  src={require('../../ui/assets/NexaFlow-logo.png')}
                  alt="NexaFlow"
                  style={{
                    paddingBottom: '25px',
                    paddingRight: '25px',
                    height: '100px',
                  }}
                />
              )}

              <MuiTitle isSize={6} tag="h2" hasTextColor="secondary">
                Two-Factor Authentication
              </MuiTitle>

              <form
                name="twoFA"
                onSubmit={this.handleSubmit}
                className="form"
                autoComplete="off"
                autoCorrect="off"
              >
                <PinInput
                  length={6}
                  type="numeric"
                  focus
                  onChange={value => this.setState({ verificationCode: value })}
                  ref={this.pinInputRef}
                />

                <Field className="has-text-centered">
                  <Button
                    type="submit"
                    isColor="gradient-bright"
                    className="is-long"
                    disabled={submitting || verificationCode.length < 6}
                    isLoading={submitting}
                  >
                    Submit
                  </Button>
                </Field>
              </form>

              <Button
                isColor="white"
                className="resend-code"
                onClick={this.resendCode}
                isLoading={sendingVerificationCode}
              >
                Resend Code
              </Button>
            </div>
          </Column>
          <Column className="right-column">
            <AsideCard>
              <Content className="has-text-grey-medium-dark">
                <MuiTitle
                  tag="h3"
                  hasTextColor="purple-light"
                  className="is-size-7"
                >
                  Check Your Phone!
                </MuiTitle>

                <p>
                  A message with a 6-digit verification code has been sent to
                  the phone number associated with your account. Please enter
                  the code to continue.
                </p>
                <p className="has-text-weight-medium">
                  <span
                    className="icon is-small"
                    style={{ verticalAlign: 'middle', marginRight: '0.25rem' }}
                  >
                    <FontAwesomeIcon icon={['fal', 'phone']} />
                  </span>
                  {supportContact && supportContact.userPhone}
                </p>
                <p>Need Support? Contact:</p>
              </Content>

              {supportContact && <SupportContactCard {...supportContact} />}
            </AsideCard>
          </Column>
          {!isWhiteLabel && (
            <Column>
              <TwoFactorImage
                image={require('../../ui/assets/TwoFactorPicture.png')}
              />
            </Column>
          )}
        </Columns>
      </Container>
    );
  }
}

// eslint-disable-next-line react/require-default-props
TwoFactorAuth.propTypes = {
  // eslint-disable-next-line react/require-default-props
  params: propTypes.object,
  history: propTypes.object.isRequired,
  location: propTypes.object,
  dispatch: propTypes.func.isRequired,
};

TwoFactorAuth.defaultProps = {
  location: {
    state: {
      email: null,
    },
  },
};

export const TwoFactorAuthPage = connect()(withNavigation(TwoFactorAuth));
